// directives/intersect.js
const states = new WeakMap()
const observerCache = new Map()
const rootCache = new Map()
const rootIds = new WeakMap()
const visualQueue = new Map()

let rootUid = 0
let visualRaf = 0

const hasWindow = typeof window !== 'undefined'
const hasDocument = typeof document !== 'undefined'
const supportsIO = hasWindow && 'IntersectionObserver' in window
const raf = hasWindow && window.requestAnimationFrame
  ? window.requestAnimationFrame.bind(window)
  : cb => setTimeout(cb, 16)
const caf = hasWindow && window.cancelAnimationFrame
  ? window.cancelAnimationFrame.bind(window)
  : clearTimeout
const reducedMotion = hasWindow && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null

const DEFAULT_CONFIG = {
  class: '',
  initialClass: 'Heziyun-hidden',
  delay: 0,
  once: true,
  threshold: 0.1,
  rootMargin: '0px',
  root: null,
  handler: null,
  useStyleHidden: true,
  transitionDuration: '320ms',
  transitionProperty: 'opacity',
  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
  respectReducedMotion: true
}

export const intersectDirective = {
  mounted: mount,
  updated: update,
  unmounted: cleanup,

  // Vue 2
  inserted: mount,
  componentUpdated: update,
  unbind: cleanup
}

function mount(el, binding) {
  const config = getConfig(binding && binding.value)
  hide(el, config)
  supportsIO ? observe(el, config) : fallback(el, config)
}

function update(el, binding) {
  const oldState = states.get(el)
  if (!oldState) return

  const config = getConfig(binding && binding.value)
  if (sameConfig(oldState.config, config)) return

  dispose(el, oldState, true)
  hide(el, config)
  supportsIO ? observe(el, config) : fallback(el, config)
}

function cleanup(el) {
  const state = states.get(el)
  if (state) dispose(el, state, false)

  visualQueue.delete(el)
  clearTimeout(el._intersectTransitionTimer)
  el._intersectTransitionTimer = 0
}

function observe(el, config) {
  let record

  try {
    record = getObserver(config)
  } catch (error) {
    report('IntersectionObserver init error:', error)
    fallback(el, config)
    return
  }

  const state = {
    config,
    record,
    timer: 0,
    raf: 0,
    active: false
  }

  states.set(el, state)
  record.count++

  const start = () => {
    state.raf = 0
    if (states.get(el) !== state || !isConnected(el)) return

    try {
      record.observer.observe(el)
    } catch (error) {
      report('IntersectionObserver observe error:', error)
      dispose(el, state, false)
      fallback(el, config)
    }
  }

  if (isConnected(el)) start()
  else state.raf = raf(start)
}

function fallback(el, config) {
  const state = {
    config,
    record: null,
    timer: 0,
    raf: 0,
    active: false
  }

  states.set(el, state)
  state.raf = raf(() => {
    state.raf = 0
    if (states.get(el) !== state || !isConnected(el)) return

    state.active = true
    queueVisual(el, config)
    callHandler(config.handler, el, {
      isIntersecting: true,
      intersectionRatio: 1,
      target: el
    })
  })
}

function onIntersect(entries) {
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const el = entry.target
    const state = states.get(el)

    if (!state || !isConnected(el)) continue

    if (!entry.isIntersecting) {
      clearDelay(state)
      if (!state.config.once) state.active = false
      continue
    }

    if (state.active) continue
    enter(el, entry, state)
  }
}

function enter(el, entry, state) {
  const trigger = () => {
    state.timer = 0

    if (
      states.get(el) !== state ||
      state.active ||
      !isConnected(el)
    ) return

    state.active = true
    queueVisual(el, state.config)
    callHandler(state.config.handler, el, entry)

    if (state.config.once) dispose(el, state, false)
  }

  if (state.config.delay > 0) {
    clearDelay(state)
    state.timer = setTimeout(trigger, state.config.delay)
  } else {
    trigger()
  }
}

function dispose(el, state, removeClasses) {
  clearDelay(state)

  if (state.raf) {
    caf(state.raf)
    state.raf = 0
  }

  if (state.record) {
    try {
      state.record.observer.unobserve(el)
    } catch (_) {}

    state.record.count = Math.max(0, state.record.count - 1)
    if (!state.record.count) {
      state.record.observer.disconnect()
      observerCache.delete(state.record.key)
    }
  }

  if (removeClasses) {
    if (state.config.class) el.classList.remove(state.config.class)
    if (state.config.initialClass) el.classList.remove(state.config.initialClass)
  }

  if (states.get(el) === state) states.delete(el)
}

function hide(el, config) {
  saveOriginalStyle(el)

  if (!config.useStyleHidden) {
    if (config.initialClass) el.classList.add(config.initialClass)
    return
  }

  applyTransition(el, config)
  el.style.opacity = '0'
  el.style.visibility = 'hidden'
}

function show(el, config) {
  const original = el._intersectOriginalStyle

  if (config.useStyleHidden) {
    applyTransition(el, config)
    el.style.visibility = original ? original.visibility : ''
    el.style.opacity = original ? original.opacity : ''
    restoreTransitionLater(el, config)
  } else if (config.initialClass) {
    el.classList.remove(config.initialClass)
  }

  if (config.class) el.classList.add(config.class)
}

function saveOriginalStyle(el) {
  if (el._intersectOriginalStyle) return

  el._intersectOriginalStyle = {
    opacity: el.style.opacity,
    visibility: el.style.visibility,
    transitionProperty: el.style.transitionProperty,
    transitionDuration: el.style.transitionDuration,
    transitionTimingFunction: el.style.transitionTimingFunction,
    willChange: el.style.willChange
  }
}

function applyTransition(el, config) {
  const noMotion = config.respectReducedMotion && prefersReducedMotion()

  el.style.transitionProperty = config.transitionProperty || 'opacity'
  el.style.transitionDuration = noMotion ? '0ms' : (config.transitionDuration || '320ms')
  el.style.transitionTimingFunction = config.transitionTimingFunction || 'ease-out'
  el.style.willChange = noMotion ? '' : 'opacity'
}

function restoreTransitionLater(el, config) {
  clearTimeout(el._intersectTransitionTimer)

  const delay = config.respectReducedMotion && prefersReducedMotion()
    ? 0
    : timeToMs(config.transitionDuration)

  if (!delay) {
    restoreTransition(el)
    return
  }

  el._intersectTransitionTimer = setTimeout(() => {
    el._intersectTransitionTimer = 0
    if (isConnected(el)) restoreTransition(el)
  }, delay + 34)
}

function restoreTransition(el) {
  const original = el._intersectOriginalStyle
  if (!original) return

  el.style.transitionProperty = original.transitionProperty
  el.style.transitionDuration = original.transitionDuration
  el.style.transitionTimingFunction = original.transitionTimingFunction
  el.style.willChange = original.willChange
}

function queueVisual(el, config) {
  visualQueue.set(el, config)
  if (!visualRaf) visualRaf = raf(flushVisuals)
}

function flushVisuals() {
  visualRaf = 0

  visualQueue.forEach((config, el) => {
    if (isConnected(el)) show(el, config)
  })

  visualQueue.clear()
}

function getObserver(config) {
  const key = observerKey(config)
  const cached = observerCache.get(key)
  if (cached) return cached

  const record = {
    key,
    count: 0,
    observer: new IntersectionObserver(onIntersect, {
      root: config.root || null,
      rootMargin: config.rootMargin,
      threshold: config.threshold
    })
  }

  observerCache.set(key, record)
  return record
}

function getConfig(value) {
  let source

  if (typeof value === 'string') source = { class: value }
  else if (typeof value === 'function') source = { handler: value }
  else if (value && typeof value === 'object') source = value
  else source = {}

  const config = {
    class: source.class || DEFAULT_CONFIG.class,
    initialClass: source.initialClass === undefined ? DEFAULT_CONFIG.initialClass : source.initialClass,
    delay: Math.max(0, parseInt(source.delay, 10) || 0),
    once: source.once !== false,
    threshold: normalizeThreshold(source.threshold === undefined ? DEFAULT_CONFIG.threshold : source.threshold),
    rootMargin: String(source.rootMargin || DEFAULT_CONFIG.rootMargin),
    root: source.root || null,
    handler: typeof source.handler === 'function' ? source.handler : null,
    useStyleHidden: source.useStyleHidden !== false,
    transitionDuration: source.transitionDuration === undefined ? DEFAULT_CONFIG.transitionDuration : source.transitionDuration,
    transitionProperty: source.transitionProperty === undefined ? DEFAULT_CONFIG.transitionProperty : source.transitionProperty,
    transitionTimingFunction: source.transitionTimingFunction === undefined
      ? DEFAULT_CONFIG.transitionTimingFunction
      : source.transitionTimingFunction,
    respectReducedMotion: source.respectReducedMotion !== false
  }

  if (typeof config.root === 'string') config.root = getRoot(config.root)
  return config
}

function normalizeThreshold(value) {
  if (!Array.isArray(value)) return clamp01(toNumber(value))

  const result = []
  for (let i = 0; i < value.length; i++) {
    const item = clamp01(toNumber(value[i]))
    if (result.indexOf(item) === -1) result.push(item)
  }

  result.sort((a, b) => a - b)
  return result.length ? result : [0]
}

function getRoot(selector) {
  if (!selector || !hasDocument) return null

  const cached = rootCache.get(selector)
  if (cached && isConnected(cached)) return cached
  if (cached) rootCache.delete(selector)

  try {
    const root = document.querySelector(selector)
    if (root) rootCache.set(selector, root)
    return root || null
  } catch (error) {
    report(`Invalid root selector: ${selector}`, error)
    return null
  }
}

function observerKey(config) {
  const threshold = Array.isArray(config.threshold)
    ? config.threshold.join(',')
    : String(config.threshold)

  return `${rootId(config.root)}|${config.rootMargin}|${threshold}`
}

function rootId(root) {
  if (!root) return 'null'

  let id = rootIds.get(root)
  if (!id) {
    id = `root_${++rootUid}`
    rootIds.set(root, id)
  }
  return id
}

function sameConfig(a, b) {
  return a.class === b.class &&
    a.initialClass === b.initialClass &&
    a.delay === b.delay &&
    a.once === b.once &&
    a.rootMargin === b.rootMargin &&
    a.root === b.root &&
    a.handler === b.handler &&
    a.useStyleHidden === b.useStyleHidden &&
    a.transitionDuration === b.transitionDuration &&
    a.transitionProperty === b.transitionProperty &&
    a.transitionTimingFunction === b.transitionTimingFunction &&
    a.respectReducedMotion === b.respectReducedMotion &&
    sameThreshold(a.threshold, b.threshold)
}

function sameThreshold(a, b) {
  const aArray = Array.isArray(a)
  const bArray = Array.isArray(b)

  if (aArray !== bArray) return false
  if (!aArray) return a === b
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function clearDelay(state) {
  if (!state.timer) return
  clearTimeout(state.timer)
  state.timer = 0
}

function callHandler(handler, el, entry) {
  if (!handler) return
  try {
    handler(el, entry)
  } catch (error) {
    report('Intersection handler error:', error)
  }
}

function isConnected(el) {
  if (!el) return false
  if (typeof el.isConnected === 'boolean') return el.isConnected
  return hasDocument && document.documentElement.contains(el)
}

function prefersReducedMotion() {
  return !!(reducedMotion && reducedMotion.matches)
}

function timeToMs(value) {
  const values = String(value || '').split(',')
  let max = 0

  for (let i = 0; i < values.length; i++) {
    const part = values[i].trim()
    const amount = parseFloat(part)
    if (!isFinite(amount)) continue

    const ms = /ms$/i.test(part) ? amount : amount * 1000
    if (ms > max) max = ms
  }

  return max
}

function toNumber(value) {
  const number = Number(value)
  return isFinite(number) ? number : 0
}

function clamp01(value) {
  return Math.min(1, Math.max(0, value))
}

function report(message, error) {
  if (typeof console !== 'undefined' && console.error) {
    console.error(message, error)
  }
}
